import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "cxfcomponents/Tables/DataTable";
import StatusBadge from "cxfcomponents/StatusBadge";
import moment from "moment";
import { useSlugCredential } from "hooks/useSlugCredential";
import { useConnectorsWithCreator } from "hooks/useConnectorsWithCreator";
import { useOwnedConnectors } from "hooks/useOwnedConnectors";
import defaultConnectorIcon from "assets/images/connector-list-view.svg";
import { useResourceDependencies } from "hooks/useResourceDependencies";

export const Connectors = () => {
   const { credential } = useSlugCredential();
   const { connectorsWithCreator, connectorCreators, handleFetchConnectorsWithCreators } = useConnectorsWithCreator();
   const { ownedConnectors } = useOwnedConnectors();
   const [searchName, setSearchName] = useState("");
   const { dependencies: connectorDependencies } = useResourceDependencies({
      sourceId: credential?.id,
      direction: "reverse",
      targetResourceType: "connector",
      returnRows: true
   });

   const tableData = useMemo(() => {
      const connectorIds = [
         ...new Set(connectorDependencies.map((dependency) => dependency.target_id))
      ];
      const credentialConnectors = connectorsWithCreator
         .filter((connector) => connectorIds.includes(connector.id))
         .filter((connector) => connector.name?.toLowerCase()?.includes(searchName.toLowerCase()));

      return {
         columns: [
            { Header: "developer_logo", accessor: "developer_logo", show: false, canGroupBy: false, disableSortBy: true },
            {
               Header: "logo",
               accessor: "logo",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value }) => (
                  <SoftBox width="60px" height="60px">
                     {value}
                  </SoftBox>
               )
            },
            {
               Header: "name",
               accessor: "name",
               width: 250,
               canGroupBy: false,
               Cell: ({ row, value }) => (
                  <SoftTypography fontSize="14px" fontWeight="bold">
                     <Link style={{ color: "#2D3748" }} className="white-to-dark" to={`/connector/${row.original.id}`}>
                        {value}
                     </Link>
                  </SoftTypography>
               )
            },
            {
               Header: "developer",
               accessor: "developer",
               width: 250,
               canGroupBy: false,
               Cell: ({ row, value }) => (
                  <SoftBox sx={{ display: "flex", alignItems: "center" }}>
                     {row.values.developer_logo}
                     <SoftTypography marginLeft="2px" fontWeight="bold" fontSize="12px">
                        {value}
                     </SoftTypography>
                  </SoftBox>
               )
            },
            {
               Header: "status",
               accessor: "status",
               canGroupBy: false,
               Cell: ({ value }) => <StatusBadge status={value?.toLowerCase() ?? "active"} />
            },
            {
               Header: "flows",
               accessor: "flows",
               canGroupBy: false,
               Cell: ({ value }) => <SoftTypography fontSize="12px">{value}</SoftTypography>
            },
            {
               Header: "last edited",
               accessor: "updated_date_time",
               width: 100,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftTypography sx={{ fontSize: "12px", display: "flex", flexDirection: "column", alignItems: "start", fontWeight: "500" }}>
                     <span>{value && moment(value).format("MM/DD/YYYY")}</span>
                     <span>{value && moment(value).format("h:mm:ss A")}</span>
                  </SoftTypography>
               )
            },
            { accessor: "id", show: false }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: credentialConnectors.map((connector) => {
            const connectorCreator = connector.connector_creator_id ? connectorCreators[connector.connector_creator_id] : null;

            return {
               id: connector.id,
               name: connector.name,
               status: connector.status,
               updated_date_time: connector.updated_date_time ?? null,
               flows: new Set(
                  connectorDependencies
                     .filter((dependency) => dependency.target_id === connector.id)
                     .map((dependency) => dependency.context_id?.split(":")?.[0])
                     .filter(Boolean)
               ).size,
               logo: <SoftBox component="img" width="100%" height="100%" borderRadius="12px" sx={{ objectFit: "contain" }} border={connector.logo ? "0.5px solid #E6E6E6" : "none"} src={connector.logo ? connector.logo : defaultConnectorIcon} />,
               developer: connectorCreator ? connectorCreator.company_name : "",
               developer_logo:
                  connectorCreator?.logo && connectorCreator?.logo !== "null" ? (
                     <SoftBox component="img" src={connectorCreator.logo} sx={{ width: "32px", height: "32px", objectFit: "contain", borderRadius: "32px" }} />
                  ) : (
                     ""
                  )
            };
         })
      };
   }, [connectorDependencies, connectorsWithCreator, connectorCreators, searchName]);

   useEffect(() => {
      handleFetchConnectorsWithCreators(ownedConnectors);
   }, [ownedConnectors]);

   return (
      <SoftBox>
         <SoftBox sx={{ alignItems: "flex-start", display: "flex", justifyContent: "flex-start", padding: "12px 0 12px 40px" }}>
            <SoftInput size="medium" placeholder="Search..." value={searchName} onChange={({ currentTarget }) => setSearchName(currentTarget.value)} sx={{ maxWidth: "380px", marginRight: "34px" }} />
         </SoftBox>
         <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons tableView="list" tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
      </SoftBox>
   );
};
